import { useCallback, useEffect, useState } from "react"

import { Refresh } from "@mui/icons-material"
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Skeleton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material"
import { type GridColDef } from "@mui/x-data-grid"
import { useNavigate } from "react-router-dom"

import {
  handleApiError,
  unhealthyCustomersService,
  type UnhealthyCategory,
  type UnhealthyCustomerEntry,
  type UnhealthyCustomersResponse,
} from "../api"
import { AdminDataGrid } from "../components/admin-data-grid"
import { DateCell, LinkCell, StatusChip } from "../components/data-grid-cells"
import { truncateId } from "../components/data-grid-utils"

import { PipelineSlaSection } from "./PipelineSlaPage"

const ORDER_STATUS_COLORS: Record<
  string,
  "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning"
> = {
  purchased: "info",
  created: "info",
  received: "primary",
  in_progress: "warning",
  completed: "success",
  failed: "error",
  cancelled: "default",
  unknown: "default",
  deleted: "default",
}

const SEVERITY_BY_CATEGORY: Record<string, "error" | "warning" | "info"> = {
  report_failed: "error",
  qc_stuck: "error",
  no_report_generated: "error",
  profile_incomplete: "warning",
  order_stalled: "warning",
}

const baseColumns: GridColDef<UnhealthyCustomerEntry>[] = [
  {
    field: "email",
    headerName: "Email",
    flex: 1.5,
    minWidth: 200,
    renderCell: (params) => (
      <LinkCell to={`/users/${params.row.userId}`}>
        {params.value}
      </LinkCell>
    ),
  },
  {
    field: "firstName",
    headerName: "First Name",
    flex: 0.7,
    minWidth: 100,
    valueGetter: (_value, row) => row.firstName ?? "-",
  },
  {
    field: "lastName",
    headerName: "Last Name",
    flex: 0.7,
    minWidth: 100,
    valueGetter: (_value, row) => row.lastName ?? "-",
  },
  {
    field: "orderId",
    headerName: "Order",
    flex: 0.8,
    minWidth: 120,
    renderCell: (params) => (
      <LinkCell to={`/orders/${params.value}`} monospace>
        {truncateId(String(params.value))}
      </LinkCell>
    ),
  },
  {
    field: "orderStatus",
    headerName: "Status",
    width: 130,
    renderCell: (params) => (
      <StatusChip
        label={String(params.value).replace(/_/g, " ").toUpperCase()}
        color={ORDER_STATUS_COLORS[params.value as string] ?? "default"}
      />
    ),
  },
  {
    field: "orderCreatedAt",
    headerName: "Order Created",
    flex: 0.8,
    minWidth: 140,
    renderCell: (params) => (
      <DateCell value={params.row.orderCreatedAt} />
    ),
  },
  {
    field: "orderUpdatedAt",
    headerName: "Last Updated",
    flex: 0.8,
    minWidth: 140,
    renderCell: (params) => (
      <DateCell value={params.row.orderUpdatedAt} />
    ),
  },
]

const COLUMNS_BY_CATEGORY: Record<string, GridColDef<UnhealthyCustomerEntry>[]> = {
  order_stalled: [
    ...baseColumns,
    {
      field: "daysSinceUpdate",
      headerName: "Days Stalled",
      width: 120,
      valueGetter: (_value, row) => (row.detail?.daysSinceUpdate as number) ?? "-",
      renderCell: (params) => {
        const days = params.value as number
        if (typeof days !== "number") return "-"
        return (
          <Chip
            label={`${days}d`}
            size="small"
            color={days > 30 ? "error" : "warning"}
            variant="outlined"
            sx={{ fontSize: "0.75rem", height: 24, fontWeight: 600 }}
          />
        )
      },
    },
  ],
  report_failed: [
    ...baseColumns,
    {
      field: "errorMessage",
      headerName: "Error",
      flex: 1,
      minWidth: 200,
      valueGetter: (_value, row) => (row.detail?.errorMessage as string) ?? "-",
      renderCell: (params) => (
        <Tooltip title={String(params.value)} placement="top-start">
          <Typography
            variant="body2"
            noWrap
            sx={{
              fontSize: "0.8rem",
              color: "error.main",
              fontFamily: "monospace",
            }}
          >
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "failedAt",
      headerName: "Failed At",
      width: 140,
      valueGetter: (_value, row) =>
        row.detail?.failedAt ? new Date(row.detail.failedAt as string).toLocaleDateString() : "-",
    },
  ],
  qc_stuck: [
    ...baseColumns,
    {
      field: "daysPending",
      headerName: "Days Pending",
      width: 120,
      valueGetter: (_value, row) => (row.detail?.daysPending as number) ?? "-",
      renderCell: (params) => {
        const days = params.value as number
        if (typeof days !== "number") return "-"
        return (
          <Chip
            label={`${days}d`}
            size="small"
            color={days > 14 ? "error" : "warning"}
            variant="outlined"
            sx={{ fontSize: "0.75rem", height: 24, fontWeight: 600 }}
          />
        )
      },
    },
  ],
}

const formatRefreshTime = (iso: string): string => {
  const date = new Date(iso)
  if (isNaN(date.getTime())) return ""
  const time = date.toTimeString().split(" ")[0]?.slice(0, 5) ?? ""
  return `${date.toISOString().split("T")[0]} ${time}`
}

type TopView = "pipeline_sla" | "unhealthy_customers"

export default function UnhealthyCustomersPage() {
  const [topView, setTopView] = useState<TopView>("pipeline_sla")
  const [data, setData] = useState<UnhealthyCustomersResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [activeTab, setActiveTab] = useState(0)
  const navigate = useNavigate()

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true)
      setError("")
      const response = await unhealthyCustomersService.getUnhealthyCustomers()
      setData(response)
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (topView === "unhealthy_customers" && !data) {
      fetchData()
    }
  }, [topView, data, fetchData])

  const totalCount = data?.categories.reduce((sum, cat) => sum + cat.customers.length, 0) ?? 0
  const activeCategory: UnhealthyCategory | undefined = data?.categories[activeTab]

  return (
    <Box sx={{ flex: 1, p: 3 }}>
      <Card sx={{ height: "fit-content" }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h4" component="h1" fontWeight="bold">
              Pipeline Health
            </Typography>
            {topView === "unhealthy_customers" && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {data?.generatedAt && (
                  <Typography variant="caption" color="text.secondary">
                    {formatRefreshTime(data.generatedAt)}
                  </Typography>
                )}
                <Tooltip title="Refresh">
                  <IconButton size="small" onClick={fetchData} disabled={isLoading}>
                    <Refresh fontSize="small" sx={{ animation: isLoading ? "spin 1s linear infinite" : "none", "@keyframes spin": { "100%": { transform: "rotate(360deg)" } } }} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>

          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2.5 }}>
            <Tabs
              value={topView}
              onChange={(_, v) => setTopView(v)}
            >
              <Tab
                value="pipeline_sla"
                label="Pipeline SLA"
                sx={{ textTransform: "none", fontSize: "0.875rem", minHeight: 48 }}
              />
              <Tab
                value="unhealthy_customers"
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <span>Unhealthy Customers</span>
                    {totalCount > 0 && (
                      <Chip
                        label={totalCount}
                        size="small"
                        color="error"
                        sx={{ height: 20, fontSize: "0.75rem", minWidth: 28 }}
                      />
                    )}
                  </Box>
                }
                sx={{ textTransform: "none", fontSize: "0.875rem", minHeight: 48 }}
              />
            </Tabs>
          </Box>

          {topView === "pipeline_sla" && <PipelineSlaSection />}

          {topView === "unhealthy_customers" && (
            <>
              {isLoading && !data ? (
                <>
                  <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Skeleton key={i} variant="rounded" width={80} height={24} />
                    ))}
                  </Box>
                  <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 1, mb: 2 }} />
                  <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} />
                </>
              ) : (
                <>
                  {data && (
                    <Box sx={{ display: "flex", gap: 1, mb: 2.5 }}>
                      {data.categories.map((cat) => (
                        <Chip
                          key={cat.id}
                          label={`${cat.label}: ${cat.customers.length}`}
                          size="small"
                          variant="outlined"
                          color={
                            cat.customers.length === 0
                              ? "success"
                              : SEVERITY_BY_CATEGORY[cat.id] ?? "warning"
                          }
                          sx={{ fontSize: "0.75rem" }}
                        />
                      ))}
                    </Box>
                  )}

                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  )}

                  {data && (
                    <>
                      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                        <Tabs
                          value={activeTab}
                          onChange={(_, v) => setActiveTab(v)}
                          variant="scrollable"
                          scrollButtons="auto"
                        >
                          {data.categories.map((cat) => (
                            <Tab
                              key={cat.id}
                              label={
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <span>{cat.label}</span>
                                  <Chip
                                    label={cat.customers.length}
                                    size="small"
                                    color={cat.customers.length > 0 ? "error" : "default"}
                                    sx={{ height: 20, fontSize: "0.75rem", minWidth: 28 }}
                                  />
                                </Box>
                              }
                              sx={{ textTransform: "none", minHeight: 48, fontSize: "0.875rem" }}
                            />
                          ))}
                        </Tabs>
                      </Box>

                      {activeCategory && (
                        <>
                          <Alert
                            severity={SEVERITY_BY_CATEGORY[activeCategory.id] ?? "info"}
                            variant="outlined"
                            sx={{ mb: 2 }}
                          >
                            {activeCategory.description}
                          </Alert>

                          {activeCategory.customers.length === 0 ? (
                            <Card
                              variant="outlined"
                              sx={{ p: 6, textAlign: "center", bgcolor: "grey.50" }}
                            >
                              <Typography variant="body1" color="text.secondary" fontWeight={500}>
                                No customers in this category
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                All clear for now
                              </Typography>
                            </Card>
                          ) : (
                            <Card variant="outlined">
                              <CardContent sx={{ p: 0 }}>
                                <AdminDataGrid<UnhealthyCustomerEntry>
                                  rows={activeCategory.customers}
                                  columns={COLUMNS_BY_CATEGORY[activeCategory.id] ?? baseColumns}
                                  getRowId={(row) => row.orderId}
                                  pageSizeOptions={[25, 50, 100]}
                                  initialState={{
                                    pagination: { paginationModel: { pageSize: 25 } },
                                  }}
                                  density="compact"
                                  onRowClick={(params) => {
                                    navigate(`/users/${params.row.userId}`)
                                  }}
                                  sx={{
                                    height: "calc(100vh - 480px)",
                                    minHeight: 300,
                                    "& .MuiDataGrid-columnHeaders": { fontWeight: 600 },
                                  }}
                                />
                              </CardContent>
                            </Card>
                          )}
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  )
}
